import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import * as constants from '../constants'
import type { Plugin } from '../plugin'

/**
 * Applies manual functionalization edits to the organization edited under the
 * joint-use current-entity target.
 */
export const updateManualFunctionalization = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) =>
    feature.actions.updateManualFunctionalization.observe().pipe(
      rxjs.withLatestFrom(
        plugin.collection.plugins.currentEntityPlugin.feature.selectors.getId({
          target: constants.CURRENT_ENTITY_TARGET,
        }).asObservable,
      ),
      rxjs.map(([action, id]) => {
        if (id === undefined) {
          return store.effects.SKIP
        }

        return plugin.collection.plugins.editingPlugin.feature.actions.edit
          .from(action)
          .create({
            id,
            makeCurrent: false,
            edit: organization => {
              if (!organization) {
                return
              }

              const jointUseInfo = organization.jointUseInfo
              if (
                !jointUseInfo ||
                !ents.extra.space.Organization.JointUseCalculationMode.isManual(
                  jointUseInfo.calculationMode,
                )
              ) {
                return
              }

              organization.jointUseInfo = {
                ...jointUseInfo,
                manualFunctionalizations: upsertFunctionalization(
                  jointUseInfo.manualFunctionalizations ?? [],
                  action.data,
                ),
              }
            },
          })
      }),
    ),
)

/**
 * Upserts a functionalization into the given array of functionalizations. If a
 * functionalization with the same functionId exists, it is replaced with the
 * new one; otherwise, the new functionalization is added to the array.
 */
function upsertFunctionalization(
  functionalizations: readonly ents.extra.space.Functionalization[],
  update: ents.extra.space.Functionalization,
): ents.extra.space.Functionalization[] {
  const existingIndex = functionalizations.findIndex(
    ({ functionId }) => functionId === update.functionId,
  )

  if (existingIndex === -1) {
    return [...functionalizations, update]
  }

  return functionalizations.map((functionalization, index) =>
    index === existingIndex ? update : functionalization,
  )
}
